// src/components/roles/RoleDetails.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  fetchRole,
  deleteRole,
  clearRoleError,
  clearRoleSuccess,
} from '../../store/actions/roleActions';
import { fetchUsers } from '../../store/actions/userActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import RolePermissionsModal from './RolePermissionsModal';

const RoleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedRole, loading, error, success } = useSelector((state) => state.roles);
  const { users } = useSelector((state) => state.users);

  const [activeTab, setActiveTab] = useState('overview');
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  // Styles object
  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    
    // Header
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem',
      border: 'none',
      background: 'none',
      color: '#6b7280',
      cursor: 'pointer',
      borderRadius: '0.375rem',
      transition: 'all 0.2s',
    },
    backButtonHover: {
      color: '#111827',
      backgroundColor: '#f3f4f6',
    },
    backIcon: {
      width: '1.5rem',
      height: '1.5rem',
    },
    title: {
      fontSize: '1.875rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
    },
    editButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#4f46e5',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    editButtonHover: {
      backgroundColor: '#4338ca',
    },
    permissionsButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#059669',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    permissionsButtonHover: {
      backgroundColor: '#047857',
    },
    deleteButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#dc2626',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    deleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    deleteButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    
    // Role Header
    roleHeader: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '1.5rem',
      marginBottom: '1.5rem',
    },
    roleName: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '0.5rem',
    },
    roleDescription: {
      fontSize: '1rem',
      color: '#6b7280',
      marginBottom: '1rem',
    },
    roleStats: {
      display: 'flex',
      gap: '1.5rem',
      flexWrap: 'wrap',
    },
    statItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    statIcon: {
      width: '1.25rem',
      height: '1.25rem',
      color: '#9ca3af',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    statValue: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginLeft: '0.25rem',
    },
    
    // Tabs
    tabsContainer: {
      display: 'flex',
      borderBottom: '1px solid #e5e7eb',
      marginBottom: '1.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem 0.5rem 0 0',
      padding: '0 1rem',
    },
    tab: {
      padding: '0.75rem 1.5rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
      background: 'none',
      border: 'none',
      borderBottom: '2px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    tabActive: {
      color: '#2563eb',
      borderBottomColor: '#2563eb',
    },
    tabHover: {
      color: '#374151',
    },
    
    // Tab Content
    tabContent: {
      backgroundColor: '#ffffff',
      borderRadius: '0 0 0.5rem 0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '1.5rem',
    },
    
    // Overview Grid
    overviewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    },
    infoCard: {
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      padding: '1rem',
    },
    cardTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    cardIcon: {
      width: '1.25rem',
      height: '1.25rem',
      color: '#6b7280',
    },
    
    // Permissions List
    permissionsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    permissionItem: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '0.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.375rem',
      border: '1px solid #e5e7eb',
    },
    permissionIcon: {
      width: '1rem',
      height: '1rem',
      color: '#10b981',
      marginRight: '0.5rem',
      marginTop: '0.125rem',
    },
    permissionContent: {
      flex: 1,
    },
    permissionName: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    },
    permissionMeta: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginTop: '0.125rem',
    },
    
    // Users List
    usersList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    userItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '0.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.375rem',
      border: '1px solid #e5e7eb',
    },
    userAvatar: {
      width: '2rem',
      height: '2rem',
      borderRadius: '9999px',
      backgroundColor: '#dbeafe',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '0.75rem',
    },
    userAvatarText: {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: '#1e40af',
    },
    userInfo: {
      flex: 1,
    },
    userName: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    },
    userEmail: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    userLink: {
      color: '#2563eb',
      textDecoration: 'none',
      fontSize: '0.875rem',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.375rem',
      transition: 'background-color 0.2s',
    },
    userLinkHover: {
      backgroundColor: '#eff6ff',
    },
    
    // Metadata Grid
    metadataGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1rem',
    },
    metadataItem: {
      display: 'flex',
      flexDirection: 'column',
    },
    metadataLabel: {
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '0.25rem',
    },
    metadataValue: {
      fontSize: '0.875rem',
      color: '#111827',
    },
    
    // Empty State
    emptyState: {
      textAlign: 'center',
      padding: '2rem',
      color: '#6b7280',
      fontSize: '0.875rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
    },
    
    // Loading Overlay
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchRole(id));
      dispatch(fetchUsers({ roleId: id, limit: 10 }));
    }
  }, [dispatch, id]);

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📋' },
    { id: 'permissions', name: 'Permissions', icon: '🔑' },
    { id: 'users', name: 'Users', icon: '👥' },
    { id: 'metadata', name: 'Metadata', icon: 'ℹ️' },
  ];

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deleteRole(id));
      navigate('/admin/roles');
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, id, navigate]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  if (loading && !selectedRole) {
    return (
      <div style={styles.loadingOverlay}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!selectedRole) {
    return (
      <div style={styles.container}>
        <Alert type="error" message="Role not found" />
        <button
          onClick={() => navigate('/admin/roles')}
          style={{ ...styles.editButton, marginTop: '1rem' }}
        >
          Back to Roles
        </button>
      </div>
    );
  }

  const hasUsers = selectedRole._count?.userRoles > 0;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate('/admin/roles')}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = styles.backButtonHover.color;
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = styles.backButton.color;
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <svg style={styles.backIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>Role Details</h1>
        </div>
        <div style={styles.headerActions}>
          <Link
            to={`/admin/roles/${id}/edit`}
            style={styles.editButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit Role
          </Link>
          <button
            onClick={() => setShowPermissionsModal(true)}
            style={styles.permissionsButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.permissionsButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.permissionsButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Manage Permissions
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={hasUsers}
            style={{
              ...styles.deleteButton,
              ...(hasUsers ? styles.deleteButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!hasUsers) {
                e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!hasUsers) {
                e.currentTarget.style.backgroundColor = styles.deleteButton.backgroundColor;
              }
            }}
          >
            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete Role
          </button>
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => dispatch(clearRoleError())} />}
      {success && <Alert type="success" message={success} onClose={() => dispatch(clearRoleSuccess())} />}

      {/* Role Header */}
      <div style={styles.roleHeader}>
        <h2 style={styles.roleName}>{selectedRole.name}</h2>
        <p style={styles.roleDescription}>{selectedRole.description || 'No description provided'}</p>
        <div style={styles.roleStats}>
          <div style={styles.statItem}>
            <svg style={styles.statIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <span style={styles.statLabel}>Users:</span>
            <span style={styles.statValue}>{selectedRole._count?.userRoles || 0}</span>
          </div>
          <div style={styles.statItem}>
            <svg style={styles.statIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span style={styles.statLabel}>Permissions:</span>
            <span style={styles.statValue}>{selectedRole.rolePermissions?.length || 0}</span>
          </div>
          <div style={styles.statItem}>
            <svg style={styles.statIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span style={styles.statLabel}>Created:</span>
            <span style={styles.statValue}>{formatDate(selectedRole.createdAt)}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabsContainer}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = styles.tabHover.color;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = styles.tab.color;
              }
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            <span style={{ marginRight: '0.5rem' }}>{tab.icon}</span>
            {tab.name}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent}>
        {activeTab === 'overview' && (
          <div style={styles.overviewGrid}>
            {/* Basic Info */}
            <div style={styles.infoCard}>
              <h3 style={styles.cardTitle}>
                <svg style={styles.cardIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Basic Information
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Role Name</div>
                  <div style={{ fontSize: '1rem', fontWeight: 500, color: '#111827' }}>{selectedRole.name}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Description</div>
                  <div style={{ fontSize: '0.875rem', color: '#4b5563' }}>{selectedRole.description || 'No description'}</div>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div style={styles.infoCard}>
              <h3 style={styles.cardTitle}>
                <svg style={styles.cardIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Statistics
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Total Users</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>
                    {selectedRole._count?.userRoles || 0}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Permissions</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>
                    {selectedRole.rolePermissions?.length || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'permissions' && (
          <div>
            <h3 style={{ ...styles.cardTitle, marginBottom: '1rem' }}>Assigned Permissions</h3>
            <div style={styles.permissionsList}>
              {selectedRole.rolePermissions?.length > 0 ? (
                selectedRole.rolePermissions.map((rp) => (
                  <div key={rp.permission.id} style={styles.permissionItem}>
                    <svg style={styles.permissionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <div style={styles.permissionContent}>
                      <div style={styles.permissionName}>{rp.permission.name}</div>
                      <div style={styles.permissionMeta}>
                        {rp.permission.module} • {rp.permission.action} • {rp.permission.resource || 'any'}
                      </div>
                      {rp.permission.description && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                          {rp.permission.description}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div style={styles.emptyState}>No permissions assigned to this role</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <h3 style={{ ...styles.cardTitle, marginBottom: '1rem' }}>Users with this Role</h3>
            <div style={styles.usersList}>
              {selectedRole.userRoles?.length > 0 ? (
                selectedRole.userRoles.map((ur) => (
                  <div key={ur.user.id} style={styles.userItem}>
                    <div style={styles.userAvatar}>
                      <span style={styles.userAvatarText}>
                        {ur.user.firstName?.[0]}{ur.user.lastName?.[0]}
                      </span>
                    </div>
                    <div style={styles.userInfo}>
                      <div style={styles.userName}>
                        {ur.user.firstName} {ur.user.lastName}
                      </div>
                      <div style={styles.userEmail}>{ur.user.email}</div>
                    </div>
                    <Link
                      to={`/admin/users/${ur.user.id}`}
                      style={styles.userLink}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = styles.userLinkHover.backgroundColor;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      View
                    </Link>
                  </div>
                ))
              ) : (
                <div style={styles.emptyState}>No users assigned to this role</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'metadata' && (
          <div>
            <h3 style={{ ...styles.cardTitle, marginBottom: '1rem' }}>Metadata</h3>
            <div style={styles.metadataGrid}>
              <div style={styles.metadataItem}>
                <span style={styles.metadataLabel}>Role ID</span>
                <span style={styles.metadataValue}>{selectedRole.id}</span>
              </div>
              <div style={styles.metadataItem}>
                <span style={styles.metadataLabel}>Created At</span>
                <span style={styles.metadataValue}>{formatDate(selectedRole.createdAt)}</span>
              </div>
              <div style={styles.metadataItem}>
                <span style={styles.metadataLabel}>Last Updated</span>
                <span style={styles.metadataValue}>{formatDate(selectedRole.updatedAt)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <RolePermissionsModal
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        role={selectedRole}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${selectedRole.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default RoleDetails;